import React, { ReactNode } from "react";
import styled, { css } from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";
import type {
  TypographyVariantPath,
  WebTheme,
} from "@ifgengineering/design-tokens/legacy";

export type V3TextColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "disabled"
  | "inverse"
  | "onColor"
  | "onBrand"
  | "onFill"
  | "brand"
  | "accent"
  | "success"
  | "error"
  | "warning"
  | "info"
  | "positive"
  | "negative";

export type V3TextAlign = "left" | "center" | "right";

export interface V3TextProps {
  variant: TypographyVariantPath;
  children: ReactNode;
  color?: V3TextColor;
  as?: keyof JSX.IntrinsicElements;
  align?: V3TextAlign;
  className?: string;
  style?: React.CSSProperties;
  testID?: string;
}

const resolveColor = (theme: WebTheme, color: V3TextColor): string => {
  // Not the numbers scale: its neutral and negative are near-black on the dark page.
  if (color === "positive") return theme.colors.ui.text.success;
  if (color === "negative") return theme.colors.ui.text.error;
  return theme.colors.ui.text[color];
};

interface StyledTextProps {
  $variant: TypographyVariantPath;
  $color: V3TextColor;
  $align?: V3TextAlign;
}

const StyledText = styled.span<StyledTextProps>`
  ${({ $variant }) => typographyCSS($variant)}
  margin: 0;
  color: ${({ theme, $color }) => resolveColor(theme, $color)};

  ${({ $align }) =>
    $align &&
    // text-align only lands on a block box, and the default element is inline
    css`
      display: block;
      text-align: ${$align};
    `}
`;

export const V3Text = ({
  variant,
  children,
  color = "primary",
  as = "span",
  align,
  className,
  style,
  testID,
}: V3TextProps): JSX.Element => (
  <StyledText
    as={as}
    $variant={variant}
    $color={color}
    $align={align}
    className={className}
    style={style}
    data-testid={testID}
  >
    {children}
  </StyledText>
);
